<template>
  <Teleport to="body">
  <div v-if="isOpen" class="modal-overlay itinerary-modal-root" @click="handleOverlayClick">
    <div class="modal-content">
      <header class="modal-header no-print">
        <div class="modal-header__title">
          <span class="material-icons modal-header__icon" aria-hidden="true">receipt_long</span>
          <div>
            <h3>{{ $t('plan_trail.actions.itinerary') }}</h3>
            <p v-if="tripTitle" class="modal-header__subtitle">{{ tripTitle }}</p>
          </div>
        </div>
        <button class="close-btn" @click="close" :aria-label="$t('shared.close')">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </header>

      <div class="modal-body itinerary-print-area">
        <p v-if="tripTitle" class="itinerary-print-title print-only">{{ tripTitle }}</p>

        <div class="itinerary-hero">
          <div class="itinerary-hero__stat">
            <span class="itinerary-hero__value">{{ itinerary.days.length }}</span>
            <span class="itinerary-hero__label">{{ itinerary.days.length === 1 ? $t('itinerary_modal.day_singular') : $t('itinerary_modal.day_plural') }}</span>
          </div>
          <div class="itinerary-hero__divider" aria-hidden="true"></div>
          <div class="itinerary-hero__stat">
            <span class="itinerary-hero__value">{{ formatDistance(itinerary.totalDistanceMeters) }}</span>
            <span class="itinerary-hero__label">{{ $t('shared.distance') }}</span>
          </div>
          <div class="itinerary-hero__divider" aria-hidden="true"></div>
          <div class="itinerary-hero__stat">
            <span class="itinerary-hero__value">{{ formatDistance(itinerary.totalAscentMeters, false) }}</span>
            <span class="itinerary-hero__label">{{ $t('plan_trail.turn_instructions.ascent') }}</span>
          </div>
          <template v-if="itinerary.totalDurationSeconds > 0">
            <div class="itinerary-hero__divider" aria-hidden="true"></div>
            <div class="itinerary-hero__stat">
              <span class="itinerary-hero__value">{{ formatDuration(itinerary.totalDurationSeconds) }}</span>
              <span class="itinerary-hero__label">{{ $t('itinerary_modal.est_time') }}</span>
            </div>
          </template>
        </div>

        <div class="itinerary-endpoints">
          <div class="itinerary-endpoint">
            <span class="material-icons itinerary-endpoint__icon" aria-hidden="true">flag</span>
            <span class="itinerary-endpoint__text">
              <small>{{ $t('plan_trail.route_endpoints.start') }}</small>
              <span>{{ pointLabel(itinerary.tripStart, itinerary.tripStartCoordinates) }}</span>
            </span>
          </div>
          <span class="material-icons itinerary-endpoints__arrow" aria-hidden="true">arrow_downward</span>
          <div class="itinerary-endpoint">
            <span class="material-icons itinerary-endpoint__icon" aria-hidden="true">sports_score</span>
            <span class="itinerary-endpoint__text">
              <small>{{ $t('plan_trail.ai_planner.finish') }}</small>
              <span>{{ pointLabel(itinerary.tripEnd, itinerary.tripEndCoordinates) }}</span>
            </span>
          </div>
        </div>

        <div v-if="itinerary.days.length === 0" class="itinerary-empty">
          <span class="material-icons" aria-hidden="true">event_busy</span>
          {{ $t('itinerary_modal.no_days_planned') }}
        </div>

        <div v-for="day in itinerary.days" :key="day.dayNumber" class="itinerary-day">
          <div class="itinerary-day__header">
            <span class="itinerary-day__badge">{{ day.dayNumber }}</span>
            <h4 class="itinerary-day__name">{{ day.name }}</h4>
          </div>

          <p v-if="day.description" class="itinerary-day__description">{{ day.description }}</p>

          <div class="itinerary-day__pills">
            <span class="itinerary-pill">
              <span class="material-icons itinerary-pill__icon" aria-hidden="true">route</span>
              {{ formatDistance(day.distanceMeters) }}
            </span>
            <span class="itinerary-pill">
              <span class="material-icons itinerary-pill__icon" aria-hidden="true">terrain</span>
              {{ formatDistance(day.ascentMeters, false) }}
            </span>
            <span v-if="day.durationSeconds > 0" class="itinerary-pill">
              <span class="material-icons itinerary-pill__icon" aria-hidden="true">schedule</span>
              {{ formatDuration(day.durationSeconds) }}
            </span>
            <span v-if="formatSunTime(day.sunrise, day.timeZone) || formatSunTime(day.sunset, day.timeZone)" class="itinerary-pill">
              ☀ {{ formatSunTime(day.sunrise, day.timeZone) || '—' }}
              <span class="itinerary-pill__divider" aria-hidden="true"></span>
              🌙 {{ formatSunTime(day.sunset, day.timeZone) || '—' }}
              <span v-if="day.sunrise && sunTimeZoneLabel(day.sunrise, day.timeZone)" class="itinerary-pill__zone">
                {{ sunTimeZoneLabel(day.sunrise, day.timeZone) }}
              </span>
            </span>
          </div>

          <div class="itinerary-day__points">
            <div class="itinerary-day__point">
              <span class="material-icons itinerary-day__point-icon" aria-hidden="true">flag</span>
              <span class="itinerary-day__point-text">
                <small>{{ $t('plan_trail.route_endpoints.start') }}</small>
                <span class="itinerary-day__point-value">{{ pointLabel(day.startLabel, day.startCoordinates) }}</span>
              </span>
            </div>
            <div class="itinerary-day__point">
              <span class="material-icons itinerary-day__point-icon" aria-hidden="true">sports_score</span>
              <span class="itinerary-day__point-text">
                <small>{{ $t('plan_trail.route_endpoints.end') }}</small>
                <span class="itinerary-day__point-value">{{ pointLabel(day.endLabel, day.endCoordinates) }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <footer class="modal-footer no-print">
        <button class="btn btn-secondary" @click="close">{{ $t('shared.close') }}</button>
        <button class="btn btn-primary" @click="handlePrint">
          <span class="material-icons itinerary-print-icon" aria-hidden="true">print</span>
          {{ $t('user_print_maps.print') }}
        </button>
      </footer>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onUnmounted, watch } from 'vue';
import type { PropType } from 'vue';
import type { ItineraryData } from '../plan_trail/services/itinerary';
import type { StageDestination } from '../plan_trail/services/stage-destinations';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import { PrettyTime } from '../../utils/helpers/HikeTime';
import { formatSunTime as formatClockTime, sunTimeZoneLabel } from '../../utils/helpers/SunTimes';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  tripTitle: {
    type: String,
    default: '',
  },
  itinerary: {
    type: Object as PropType<ItineraryData>,
    required: true,
  },
});

const emit = defineEmits(['close']);

const PRINT_CLASS = 'itinerary-printing';

// Falls back to raw coordinates (matching POIDialog.vue's "lat, lon" @ 6
// decimal places convention) when no POI/waypoint/highlight is attached —
// still better than a bare em dash, since the point itself always exists.
// Callers with no route geometry to derive coordinates from (e.g. the saved
// hike overview page) simply never pass any, and just get the em dash.
function pointLabel(destination: StageDestination | null, coordinates: [number, number] | null = null): string {
  if (destination?.name) return destination.name;
  if (coordinates) return `${coordinates[1].toFixed(6)}, ${coordinates[0].toFixed(6)}`;
  return '—';
}

function formatDistance(meters: number, convertToBigUnitsAfter1000m = true): string {
  return MeasurementPrefUtility.preference().smallUnitFormatter(meters, convertToBigUnitsAfter1000m);
}

function formatDuration(seconds: number): string {
  return new PrettyTime(seconds).prettyTime();
}

function formatSunTime(date: Date | null, timeZone: string | null): string | null {
  return date ? formatClockTime(date, timeZone) : null;
}

function cleanupPrintClass() {
  document.body.classList.remove(PRINT_CLASS);
}

function handlePrint() {
  document.body.classList.add(PRINT_CLASS);
  window.addEventListener('afterprint', cleanupPrintClass, { once: true });
  window.print();
}

// Safety net in case `afterprint` never fires (e.g. print dialog cancelled
// in a way the browser doesn't report) — clean up whenever the modal closes
// or unmounts so the print class never lingers on <body>.
watch(() => props.isOpen, (open) => {
  if (!open) cleanupPrintClass();
});
onUnmounted(cleanupPrintClass);

function close() {
  emit('close');
}

function handleOverlayClick(event: MouseEvent) {
  if (event.target === event.currentTarget) {
    close();
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--dark, #17181d);
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
  max-width: 640px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header__title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-header__icon {
  font-size: 26px;
  color: #fddf00;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.2;
}

.modal-header__subtitle {
  margin: 0.1rem 0 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  transition: color 0.2s ease, background 0.2s ease;
}

.close-btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.modal-body {
  padding: 1.25rem 1.5rem 1.5rem;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.itinerary-print-icon {
  font-size: 18px;
  vertical-align: -4px;
  margin-right: 0.3rem;
}

/* Only ever shown in print output (see .itinerary-print-title below) */
.print-only {
  display: none;
}

/* ── Hero stat row ─────────────────────────────────────────────────── */

.itinerary-hero {
  display: flex;
  align-items: center;
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 0.9rem 0.5rem;
  margin-bottom: 1rem;
}

.itinerary-hero__stat {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  min-width: 0;
}

.itinerary-hero__value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
}

.itinerary-hero__label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.55);
}

.itinerary-hero__divider {
  width: 1px;
  align-self: stretch;
  background: rgba(255, 255, 255, 0.1);
  margin: 0.1rem 0;
}

/* ── Trip start/finish row ─────────────────────────────────────────── */

.itinerary-endpoints {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
}

.itinerary-endpoint {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
  padding: 0.55rem 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.itinerary-endpoint__icon {
  flex: 0 0 auto;
  font-size: 20px;
  color: #fddf00;
}

.itinerary-endpoint__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.itinerary-endpoint__text small {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.5);
}

.itinerary-endpoint__text span {
  font-size: 0.85rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.itinerary-endpoints__arrow {
  flex: 0 0 auto;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.35);
  transform: rotate(-90deg);
}

.itinerary-empty {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.55);
  padding: 1.5rem 0;
  justify-content: center;
}

/* ── Day cards — same visual language as the day list's .day-card ──── */

.itinerary-day {
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 0.9rem 1rem;
  margin-bottom: 0.75rem;
}

.itinerary-day:last-child {
  margin-bottom: 0;
}

.itinerary-day__header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.35rem;
}

.itinerary-day__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: #fddf00;
  color: #17181d;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1;
}

.itinerary-day__name {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
}

.itinerary-day__description {
  margin: 0 0 0.6rem;
  padding-left: 2.1rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.4;
}

.itinerary-day__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding-left: 2.1rem;
  margin-bottom: 0.6rem;
}

.itinerary-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
}

.itinerary-pill__icon {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.55);
}

.itinerary-pill__zone {
  font-size: 0.85em;
  opacity: 0.6;
}

.itinerary-pill__divider {
  width: 1px;
  height: 0.8em;
  background: rgba(255, 255, 255, 0.15);
}

.itinerary-day__points {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
  padding-left: 2.1rem;
}

.itinerary-day__point {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.itinerary-day__point-icon {
  flex: 0 0 auto;
  font-size: 17px;
  color: #fddf00;
}

.itinerary-day__point-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.itinerary-day__point-text small {
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.5);
}

.itinerary-day__point-value {
  font-size: 0.82rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Screen-only visibility toggle for print-only chrome removal. Scoped is
   safe here since it only ever needs to match elements inside this
   component's own template. */
@media print {
  .no-print {
    display: none !important;
  }
}
</style>

<style>
/*
 * Deliberately NOT scoped — needs to reach document.body, which sits
 * outside this component's own DOM subtree and so can't be targeted by
 * Vue's scoped data-v-* attribute rewriting.
 *
 * Every selector is gated behind body.itinerary-printing so this rule is
 * inert on every other page/print job in the app: the class is only added
 * by handlePrint() for the few seconds printing is actually active, then
 * removed on the `afterprint` event (or when the modal closes/unmounts). A
 * bare rule with no gate would instead apply globally the moment this
 * component is part of the bundle, breaking print output anywhere else
 * that shares it.
 *
 * Two things this had to get right, both learned the hard way:
 *
 * 1. HIDE THE REST OF THE PAGE WITH display:none, NOT visibility:hidden.
 *    visibility:hidden makes an element invisible but still reserves its
 *    full layout box — so on a long host page (e.g. the hike overview page,
 *    with its hero/map/comments sections above this modal in the DOM), all
 *    that now-invisible content still occupies its normal height in the
 *    print layout, pushing this modal's actual content down by however many
 *    blank pages that hidden content would otherwise have spanned. This is
 *    exactly what caused an earlier bug where the printout started several
 *    pages in instead of on page 1. display:none fully collapses the box,
 *    so nothing above the modal reserves any space.
 *
 *    display:none can't be "un-hidden" on a descendant the way visibility
 *    can (a hidden ancestor suppresses the whole subtree, full stop), so we
 *    can only hide the modal's SIBLINGS, not an ancestor of it — which means
 *    the modal itself must be a direct child of <body>. Hence the
 *    <Teleport to="body"> in the template, and the ">" combinator below
 *    (only direct children of body are considered "everything else").
 *
 * 2. RESET THE MODAL'S OWN FIXED/FLEX OVERLAY CHROME BACK TO STATIC BLOCK
 *    FLOW. An earlier version gave `.itinerary-print-area` `position:
 *    absolute` so it would fill the page after everything else was hidden —
 *    but its containing block was `.modal-overlay`, which is `position:
 *    fixed`. Browsers' print engines handle `position: fixed`/`absolute`
 *    content unreliably (it's commonly clipped to a single page or dropped
 *    entirely), which produced a blank printout. Resetting the whole modal
 *    chain to plain static flow lets the browser paginate the itinerary like
 *    an ordinary flowing document.
 */
@media print {
  body.itinerary-printing > *:not(.itinerary-modal-root) {
    display: none !important;
  }

  .itinerary-modal-root .no-print {
    display: none !important;
  }

  .itinerary-modal-root {
    position: static !important;
    display: block !important;
    background: none !important;
    padding: 0 !important;
  }
  .itinerary-modal-root .modal-content {
    position: static !important;
    max-width: none !important;
    width: auto !important;
    max-height: none !important;
    overflow: visible !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    background: #fff !important;
  }
  .itinerary-modal-root .itinerary-print-area {
    overflow: visible !important;
    padding: 0 !important;
    color: #000 !important;
  }

  /* Print-friendly palette — dark-theme surfaces would otherwise waste ink
     and can render as solid black blocks on some printers. */
  .itinerary-modal-root .itinerary-print-title {
    display: block !important;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0 0 1rem;
    color: #000 !important;
  }
  .itinerary-modal-root .itinerary-hero,
  .itinerary-modal-root .itinerary-endpoint,
  .itinerary-modal-root .itinerary-day {
    background: #fff !important;
    border-color: #ccc !important;
    color: #000 !important;
  }
  .itinerary-modal-root .itinerary-hero__value,
  .itinerary-modal-root .itinerary-day__name,
  .itinerary-modal-root .itinerary-endpoint__text span {
    color: #000 !important;
  }
  .itinerary-modal-root .itinerary-hero__label,
  .itinerary-modal-root .itinerary-endpoint__text small,
  .itinerary-modal-root .itinerary-day__point-text small,
  .itinerary-modal-root .itinerary-day__description {
    color: #444 !important;
  }
  .itinerary-modal-root .itinerary-day__badge {
    background: #000 !important;
    color: #fff !important;
  }
  .itinerary-modal-root .itinerary-pill {
    background: #f3f3f3 !important;
    border-color: #ddd !important;
    color: #000 !important;
  }
  .itinerary-modal-root .itinerary-day__point-icon,
  .itinerary-modal-root .itinerary-endpoint__icon,
  .itinerary-modal-root .itinerary-pill__icon {
    color: #000 !important;
  }

  /* Don't split a day card awkwardly across a page boundary. */
  .itinerary-modal-root .itinerary-day {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>
